<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { CharacterHistoryTimelineOptions } from '../types/timeline';
import type { EntitySummary } from '../types/entities';
import EntityPicker from './EntityPicker.vue';

const props = withDefaults(defineProps<{
  sceneId?: string | null;
  segmentId?: string | null;
  entities: EntitySummary[];
  timelineData: Pick<CharacterHistoryTimelineOptions, 'chapters' | 'scenes' | 'segments'> | null;
  emptySceneLabel?: string;
  emptySegmentLabel?: string;
}>(), { sceneId: null, segmentId: null, emptySceneLabel: 'Keine Szene', emptySegmentLabel: 'Ganzes Szenenereignis' });
const emit = defineEmits<{ 'update:sceneId': [value: string | null]; 'update:segmentId': [value: string | null] }>();
const chapterFilterId = ref<string | null>(null);

const timelineSceneIds = computed(() => new Set((props.timelineData?.scenes ?? []).map((scene) => scene.id)));
const chapterEntities = computed(() => {
  const ids = new Set((props.timelineData?.chapters ?? []).map((chapter) => chapter.id));
  return props.entities.filter((entity) => ids.has(entity.id));
});
const sceneEntities = computed(() => {
  const allowed = new Set((props.timelineData?.scenes ?? []).filter((scene) => !chapterFilterId.value || scene.chapterId === chapterFilterId.value).map((scene) => scene.id));
  const chapters = new Map((props.timelineData?.chapters ?? []).map((chapter) => [chapter.id, chapter]));
  const scenes = new Map((props.timelineData?.scenes ?? []).map((scene) => [scene.id, scene]));
  return props.entities.filter((entity) => timelineSceneIds.value.has(entity.id) && allowed.has(entity.id)).map((entity) => {
    const scene = scenes.get(entity.id); const chapter = scene?.chapterId ? chapters.get(scene.chapterId) : null;
    return { ...entity, searchContext: `${chapter?.title ?? 'Ohne Kapitel'} · Erzählposition ${scene?.chapterPosition ?? '–'}` };
  });
});
const sceneSortOrder = computed(() => {
  const chapterOrder = new Map((props.timelineData?.chapters ?? []).map((chapter, index) => [chapter.id, index]));
  return Object.fromEntries((props.timelineData?.scenes ?? []).map((scene) => [scene.id,
    ((scene.chapterId ? chapterOrder.get(scene.chapterId) : undefined) ?? 9_999) * 1_000_000 + scene.chapterPosition]));
});
const orderedSegments = computed(() => (props.timelineData?.segments ?? [])
  .filter((segment) => segment.sceneId === props.sceneId)
  .sort((left, right) => left.sortOrder - right.sortOrder));
const segmentSortOrder = computed(() => Object.fromEntries(orderedSegments.value.map((segment, index) => [segment.id, index])));
const segmentEntities = computed<EntitySummary[]>(() => orderedSegments.value
  .map((segment, index) => {
    const scene = props.timelineData?.scenes.find((candidate) => candidate.id === segment.sceneId);
    return {
    id: segment.id, workspaceId: '', templateId: 'timeline-segment', templateKey: 'segment', templateCategory: 'segment',
    title: segment.label, summary: null, aiSummary: null, profileImageData: null, status: 'active', icon: null,
    searchContext: `Szene · ${scene?.title ?? 'Unbekannte Szene'} · Position ${index + 1}`
  };
  }));

function chooseChapter(value: string | null) {
  chapterFilterId.value = value;
  const current = props.timelineData?.scenes.find((scene) => scene.id === props.sceneId);
  if (value && current?.chapterId !== value) { emit('update:sceneId', null); emit('update:segmentId', null); }
}
function chooseScene(value: string | null) {
  emit('update:sceneId', value);
  emit('update:segmentId', null);
  const chapterId = props.timelineData?.scenes.find((scene) => scene.id === value)?.chapterId;
  if (value && chapterId) chapterFilterId.value = chapterId;
}

watch(() => props.sceneId, (sceneId) => {
  if (!sceneId) return;
  const chapterId = props.timelineData?.scenes.find((scene) => scene.id === sceneId)?.chapterId;
  if (chapterId) chapterFilterId.value = chapterId;
}, { immediate: true });
</script>

<template>
  <div class="timeline-anchor-picker">
    <label><span>Kapitel filtern</span><EntityPicker :model-value="chapterFilterId" :entities="chapterEntities" clear-label="Alle Kapitel" placeholder="Kapitel suchen …" aria-label="Kapitel filtern" @update:model-value="chooseChapter" /></label>
    <label><span>Szene</span><EntityPicker :model-value="sceneId" :entities="sceneEntities" :sort-order="sceneSortOrder" :disabled="!sceneEntities.length" :clear-label="emptySceneLabel" placeholder="Szene suchen …" aria-label="Szene auswählen" @update:model-value="chooseScene" /></label>
    <label><span>Segment</span><EntityPicker :model-value="segmentId" :entities="segmentEntities" :sort-order="segmentSortOrder" :disabled="!sceneId" :clear-label="emptySegmentLabel" placeholder="Segment suchen …" aria-label="Segment auswählen" @update:model-value="emit('update:segmentId', $event)" /></label>
  </div>
</template>

<style scoped>
.timeline-anchor-picker{display:grid;grid-template-columns:minmax(150px,.8fr) minmax(190px,1.2fr) minmax(170px,1fr);gap:8px}.timeline-anchor-picker>label{display:grid;min-width:0;gap:4px}.timeline-anchor-picker>label>span{color:var(--ui-text-muted);font-size:8px;font-weight:850;text-transform:uppercase}@media(max-width:760px){.timeline-anchor-picker{grid-template-columns:1fr}}
</style>
